import { ThemeEvents } from '@theme/events';

/**
 * Weight gate — bascule "Ajouter au panier" → "Demander un devis" quand le poids projeté
 * (panier actuel + quantité saisie × poids de la variante) atteint le seuil configuré.
 *
 * Attributs attendus (rendus en Liquid) :
 *  data-enabled          "true" | "false"
 *  data-threshold        seuil en grammes
 *  data-cart-weight      poids actuel du panier en grammes (cart.total_weight)
 *  data-product-id       id du produit
 *  data-product-handle   handle du produit
 *  data-quote-url        URL de la page de demande de devis
 *  data-variants         JSON { "<variant_id>": <grammes>, ... }
 *  data-message-template texte avec le jeton [weight]
 *
 * Éléments internes :
 *  input[name="id"], input[name="quantity"]  (formulaire produit Horizon)
 *  [ref="weightGateLink"], [ref="weightGateMessage"]
 */
class WeightGateComponent extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;
  /** @type {number | null} */
  #evaluateTimer = null;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });
    document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    this.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#scheduleEvaluate, { signal });
    this.addEventListener('input', this.#scheduleEvaluate, { signal });
    this.addEventListener('change', this.#scheduleEvaluate, { signal });
    this.addEventListener('submit', this.#onSubmit, { signal, capture: true });
    this.addEventListener('click', this.#onClick, { signal });

    this.#evaluate();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
  }

  get enabled() {
    return this.dataset.enabled === 'true';
  }

  /** Seuil en grammes */
  get threshold() {
    return Number(this.dataset.threshold) || 0;
  }

  /** Poids du panier en grammes */
  get cartWeight() {
    return Number(this.dataset.cartWeight) || 0;
  }

  /** @returns {Record<string, number>} */
  get variantWeights() {
    try {
      return JSON.parse(this.dataset.variants || '{}');
    } catch {
      return {};
    }
  }

  get variantId() {
    const input = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    return input?.value || '';
  }

  get quantity() {
    const input = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="quantity"]'));
    const value = parseInt(input?.value || '1', 10);
    return Number.isFinite(value) && value > 0 ? value : 1;
  }

  get isOver() {
    return this.hasAttribute('data-over');
  }

  /** Poids projeté en grammes */
  get projectedWeight() {
    const unit = this.variantWeights[this.variantId] || 0;
    return this.cartWeight + unit * this.quantity;
  }

  /** URL de la page devis avec la sélection en cours */
  buildQuoteUrl() {
    const url = new URL(this.dataset.quoteUrl || '/pages/demande-de-devis', window.location.origin);
    url.searchParams.set('source', 'product');
    if (this.dataset.productHandle) url.searchParams.set('product', this.dataset.productHandle);
    if (this.variantId) url.searchParams.set('variant', this.variantId);
    url.searchParams.set('qty', String(this.quantity));
    return url.toString();
  }

  #scheduleEvaluate = () => {
    if (this.#evaluateTimer) clearTimeout(this.#evaluateTimer);
    this.#evaluateTimer = window.setTimeout(() => this.#evaluate(), 30);
  };

  #evaluate() {
    if (!this.enabled || this.threshold <= 0) {
      this.#setState(false, 0);
      return;
    }
    const projected = this.projectedWeight;
    this.#setState(projected >= this.threshold, projected);
  }

  /**
   * @param {boolean} over
   * @param {number} projected
   */
  #setState(over, projected) {
    this.toggleAttribute('data-over', over);

    const link = /** @type {HTMLAnchorElement | null} */ (this.querySelector('[ref="weightGateLink"]'));
    if (link) link.href = this.buildQuoteUrl();

    const message = this.querySelector('[ref="weightGateMessage"]');
    if (message) {
      const kg = (projected / 1000).toLocaleString(document.documentElement.lang || 'fr', {
        maximumFractionDigits: 1,
      });
      message.textContent = (this.dataset.messageTemplate || '').replace('[weight]', kg);
    }

    // Barre sticky (fiche produit) : elle "pilote" le bouton principal, on adapte son libellé.
    const stickyButton = document.querySelector('sticky-add-to-cart [ref="addToCartButton"]');
    if (stickyButton) {
      stickyButton.toggleAttribute('data-weight-gate-over', over);
      const label = stickyButton.querySelector('.add-to-cart-text__content span span');
      if (label) {
        if (over) {
          if (!stickyButton.dataset.originalLabel) stickyButton.dataset.originalLabel = label.textContent || '';
          label.textContent = this.dataset.quoteLabel || label.textContent;
        } else if (stickyButton.dataset.originalLabel) {
          label.textContent = stickyButton.dataset.originalLabel;
        }
      }
    }
  }

  /** @param {Event} event */
  #onCartUpdate = (event) => {
    const detail = /** @type {any} */ (event).detail;
    const totalWeight = detail?.resource?.total_weight;
    if (typeof totalWeight === 'number') {
      this.dataset.cartWeight = String(totalWeight);
      this.#evaluate();
    } else {
      this.#refreshCartWeight();
    }
  };

  async #refreshCartWeight() {
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const cart = await response.json();
      if (typeof cart.total_weight === 'number') {
        this.dataset.cartWeight = String(cart.total_weight);
        this.#evaluate();
      }
    } catch (error) {
      console.error('[weight-gate] cart refresh failed', error);
    }
  }

  /** @param {Event} event */
  #onVariantUpdate = (event) => {
    const detail = /** @type {any} */ (event).detail;
    const productId = detail?.data?.productId;
    if (productId && this.dataset.productId && String(productId) !== String(this.dataset.productId)) return;
    // Le formulaire est morphé de façon asynchrone par product-form.js : on réévalue après.
    window.setTimeout(() => this.#evaluate(), 80);
    window.setTimeout(() => this.#evaluate(), 400);
  };

  /** @param {Event} event */
  #onSubmit = (event) => {
    if (!this.isOver) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    window.location.assign(this.buildQuoteUrl());
  };

  /** @param {Event} event */
  #onClick = (event) => {
    const target = /** @type {HTMLElement | null} */ (event.target);
    const link = target?.closest('[ref="weightGateLink"]');
    if (!(link instanceof HTMLAnchorElement)) return;
    link.href = this.buildQuoteUrl();
  };
}

if (!customElements.get('weight-gate-component')) {
  customElements.define('weight-gate-component', WeightGateComponent);
}
